import { Typography, CircularProgress, Grid, Chip } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import { Wrapper } from "components/wrapper";
import { DetailCard, DetailField, DetailSectionTitle } from "components/detailPage";
import { ExportButtons } from "components/exportButtons";
import { useEmendaParlamentarDetailsState } from "./context/emenda-parlamentar-details-context";
import { useEmendaParlamentarDetailsController } from "./context/emenda-parlamentar-details-controller";

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '-';

export function EmendaParlamentarDetailsPage() {
  const { emendaDetails, loading, error } = useEmendaParlamentarDetailsState();
  const { loadEmendaDetails, clearEmendaDetails, exportEmendaDetail } = useEmendaParlamentarDetailsController();
  let { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (id) {
      loadEmendaDetails(parseInt(id));
    }

    return () => {
      clearEmendaDetails();
    };
  }, [id, loadEmendaDetails, clearEmendaDetails]);

  if (loading) {
    return (
      <Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Typography color="error" variant="h6">{error}</Typography>
      </Wrapper>
    );
  }

  if (!emendaDetails) {
    return (
      <Wrapper>
        <Typography variant="h6">Emenda parlamentar não encontrada.</Typography>
      </Wrapper>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Emendas Parlamentares", href: "/emendas-parlamentares" },
          { label: emendaDetails.numeroEmenda },
        ]}
      />
      <PageTitle>EMENDA {emendaDetails.numeroEmenda}</PageTitle>
      <Wrapper>
        <ExportButtons onExport={(format) => exportEmendaDetail(Number(id), format)} />
        <DetailCard>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Chip label={emendaDetails.tipoEmendaName} color="primary" />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Autor" value={emendaDetails.nomAutor} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Órgão" value={emendaDetails.orgaoName} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Exercício" value={emendaDetails.ano} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Forma de Repasse" value={emendaDetails.formaRepasseEmendaName} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Função de Governo" value={emendaDetails.funcaoGovernoName} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DetailField label="Tipo de Origem" value={emendaDetails.tipoOrigem} />
            </Grid>

            {emendaDetails.convenioNumero && (
              <Grid item xs={12} sm={4}>
                <DetailField label="Convênio" value={emendaDetails.convenioNumero} />
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Valores</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DetailField label="Valor Previsto" value={formatCurrency(emendaDetails.valorPrevisto)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DetailField label="Valor Realizado" value={formatCurrency(emendaDetails.valorRealizado)} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Detalhes</DetailSectionTitle>
            </Grid>

            <Grid item xs={12}>
              <DetailField label="Objeto" value={emendaDetails.textoObjetoEmenda} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Execução (Beneficiários/Credores)</DetailSectionTitle>
              {emendaDetails.execucoes && emendaDetails.execucoes.length > 0 ? (
                emendaDetails.execucoes
                  .slice()
                  .sort((a, b) => new Date(a.dtaEmpenho).getTime() - new Date(b.dtaEmpenho).getTime())
                  .map((execucao) => (
                    <Typography key={execucao.id} variant="body2" style={{ marginBottom: 8 }}>
                      {execucao.nomBeneficiarioCredor} — Empenho {execucao.numEmpenho} ({new Date(execucao.dtaEmpenho).toLocaleDateString('pt-BR')})
                      {` | Empenhado: ${formatCurrency(execucao.valorEmpenhado)}`}
                      {execucao.valorLiquidado != null ? ` | Liquidado: ${formatCurrency(execucao.valorLiquidado)}` : ''}
                      {execucao.valorPago != null ? ` | Pago: ${formatCurrency(execucao.valorPago)}` : ''}
                    </Typography>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhuma execução registrada.</Typography>
              )}
            </Grid>
          </Grid>
        </DetailCard>
      </Wrapper>
    </>
  );
}
